'use client'

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react'
import { useRouter } from 'next/navigation'

interface TransitionContextType {
  navigateTo: (href: string) => void
  // We'll use this state later in the layout if needed
  isTransitioning: boolean
}

const TransitionContext = createContext<TransitionContextType | undefined>(undefined)

export const useTransition = () => {
  const context = useContext(TransitionContext)
  if (context === undefined) {
    throw new Error('useTransition must be used within a TransitionProvider')
  }
  return context
}

export const TransitionProvider = ({ children }: { children: ReactNode }) => {
  const router = useRouter()
  const [isTransitioning, setIsTransitioning] = useState(false)
  // We don't store the target path here because the layout handles the children prop flow

  const navigateTo = useCallback(
    (href: string) => {
      setIsTransitioning(true)
      // The actual routing happens later, after the exit animation completes in the layout.
      // We just signal that a transition is desired.
      router.push(href) // We actually push immediately, but rely on the layout interception below
    },
    [router],
  )

  return (
    <TransitionContext.Provider value={{ navigateTo, isTransitioning }}>
      {children}
    </TransitionContext.Provider>
  )
}
